import { LayoutLoading } from '@hs-baumappe/frontend-kit';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { ErrorRenderer } from '@hs-baumappe/redkit';

import useOvertimeEditDetail from '../../useOvertimeEditDetail';
import useOvertimeSave from '../../../../hooks/useOvertimeSave';
import ErrorState from '../../../../components/ErrorState';
import OvertimeForm from '../../../../components/OvertimeForm';
import { OvertimeProject } from '../../../../graphql/__generated__/OvertimeProject.fragment';
import { LaunchDarklyFlagSet } from '../../../../launchdarkly/launchDarklyFlagSet';
import { mapOvertimeToFormValues } from '../../OvertimeEdit.utils';

interface OvertimeEditBodyProps {
  overtimeId: string;
  project: OvertimeProject;
  onFormDirtyStateChange: (dirty: boolean) => void;
}

export default function OvertimeEditBody({
  overtimeId,
  project,
  onFormDirtyStateChange,
}: OvertimeEditBodyProps): JSX.Element | null {
  const { t } = useTranslation();
  const { overtimePdfRedesign: pdfRedesignEnabled } = useFlags<LaunchDarklyFlagSet>();

  const { overtime, employees, professions, employeeAutomaticRoleSelection, loading, error } =
    useOvertimeEditDetail({
      overtimeId,
      projectId: project.id,
      pdfRedesignEnabled,
    });

  const {
    save,
    loading: saving,
    error: saveError,
  } = useOvertimeSave({
    overtimeId,
    draftProject: project.draft,
    pdfRedesignEnabled,
  });

  if (loading) {
    return <LayoutLoading />;
  }

  if (error) {
    return <ErrorRenderer apolloError={error} />;
  }

  if (!overtime) {
    return null;
  }

  const hsProjectNumber = project.hsProjectNumber ?? undefined;

  return (
    <>
      {saveError && <ErrorState error={saveError} />}

      <OvertimeForm
        mode="edit"
        initialValues={mapOvertimeToFormValues({
          overtime,
          hsProjectNumber,
          pdfRedesignEnabled,
          isDraftProject: project.draft,
        })}
        projectId={project.id}
        employees={employees}
        professions={professions}
        disableProjectNumberField={!project.draft}
        nextSuggestedNumber={overtime.no}
        submitButtonText={t('updateOvertimeForm.submitButton')}
        onSubmit={save}
        onDirtyStateChange={onFormDirtyStateChange}
        loading={saving}
        pdfRedesignEnabled={pdfRedesignEnabled}
        employeeAutomaticRoleSelection={employeeAutomaticRoleSelection}
      />
    </>
  );
}
